// apps/web/src/adapters/migrateLocalStorage.ts
// ─────────────────────────────────────────────────────────────────────────
// One-time migration from localStorage (old key/value scheme) to IndexedDB
// (Dexie tables). Runs once on first app boot after the schema change.
// Idempotent: a flag is written to localStorage to prevent re-running.
// ─────────────────────────────────────────────────────────────────────────

import { STORAGE_TABLES } from '@nisk/core';
import { storage } from './WebStorageAdapter';

const MIGRATION_FLAG = 'nis.migrations.v1.dexie';

interface MigrationStats {
	ranAt: number;
	lessonsProgress: number;
	quizResults: number;
	srsRecords: number;
	settings: number;
	economy: number;
}

export async function migrateFromLocalStorage(): Promise<MigrationStats | null> {
	if (typeof window === 'undefined') return null;
	if (localStorage.getItem(MIGRATION_FLAG)) return null;

	const stats: MigrationStats = {
		ranAt: Date.now(),
		lessonsProgress: 0,
		quizResults: 0,
		srsRecords: 0,
		settings: 0,
		economy: 0,
	};

	const keys = Object.keys(localStorage);

	for (const key of keys) {
		const value = localStorage.getItem(key);
		if (value === null) continue;

		try {
			// ── Lesson completion: nis.lesson.{moduleId}.{lessonId} = 'true'
			if (key.startsWith('nis.lesson.')) {
				const parts = key.replace('nis.lesson.', '').split('.');
				if (parts.length >= 2) {
					const lessonId = parts[parts.length - 1];
					await storage.set(
						STORAGE_TABLES.PROGRESS,
						`lesson:${lessonId}`,
						{
							lessonId,
							state: 'read',
							readAt: new Date().toISOString(),
						},
					);
					stats.lessonsProgress += 1;
				}
				continue;
			}

			// ── Quiz result: nis.quiz.{moduleId} = JSON
			if (key.startsWith('nis.quiz.')) {
				const moduleId = key.replace('nis.quiz.', '');
				try {
					const parsed = JSON.parse(value);
					await storage.set(
						STORAGE_TABLES.PROGRESS,
						`quiz:${moduleId}`,
						parsed,
					);
					stats.quizResults += 1;
				} catch {
					/* skip malformed */
				}
				continue;
			}

			// ── SRS records: nis.srs.{cardId} = JSON
			if (key.startsWith('nis.srs.')) {
				const cardId = key.replace('nis.srs.', '');
				try {
					const parsed = JSON.parse(value);
					await storage.set(
						STORAGE_TABLES.SRS_RECORDS,
						cardId,
						parsed,
					);
					stats.srsRecords += 1;
				} catch {
					/* skip */
				}
				continue;
			}

			// ── Gamification: streak + XP
			if (
				key === 'nis.gamification.streak' ||
				key === 'nis.gamification.xp'
			) {
				try {
					const parsed = JSON.parse(value);
					await storage.set(
						STORAGE_TABLES.ECONOMY,
						key.replace('nis.gamification.', ''),
						parsed,
					);
					stats.economy += 1;
				} catch {
					/* skip */
				}
				continue;
			}

			// ── Settings: nis.settings.*
			if (key.startsWith('nis.settings.')) {
				const settingKey = key.replace('nis.settings.', '');
				await storage.set(STORAGE_TABLES.SETTINGS, settingKey, value);
				stats.settings += 1;
				continue;
			}
		} catch (err) {
			console.warn(`Migration: failed to process key ${key}`, err);
		}
	}

	localStorage.setItem(MIGRATION_FLAG, JSON.stringify(stats));
	console.log('[Nisk] localStorage → IndexedDB migration complete', stats);
	return stats;
}
